type FirmAccessUser = {
  firm_id?: number;
  is_finance_user?: boolean;
  role?: string;
  firm?: { id: number };
  firms?: Array<{ id: number }>;
  roles?: Array<{ slug: string }>;
};

export const getUserFirmIds = (user?: FirmAccessUser | null): number[] => {
  if (!user) return [];
  const ids = new Set<number>();
  if (user.firm_id) ids.add(Number(user.firm_id));
  if (user.firm?.id) ids.add(Number(user.firm.id));
  (user.firms || []).forEach((firm) => {
    if (firm?.id) ids.add(Number(firm.id));
  });
  return Array.from(ids);
};

export const isPmpAdminUser = (user?: FirmAccessUser | null): boolean =>
  user?.role === 'pmp_admin' || !!user?.roles?.some((role) => role.slug === 'pmp_admin');

export const isSiteAdminUser = (user?: FirmAccessUser | null): boolean =>
  user?.role === 'site_admin' || !!user?.roles?.some((role) => role.slug === 'site_admin');

export const isPmpOrSiteAdminUser = (user?: FirmAccessUser | null): boolean =>
  isPmpAdminUser(user) || isSiteAdminUser(user);

export const canSeeVendorBillingAmounts = (user?: FirmAccessUser | null): boolean =>
  isPmpAdminUser(user) || !isSiteAdminUser(user);

export const userCanAccessFirm = (
  user: FirmAccessUser | null | undefined,
  firmId: number | string | undefined | null
): boolean => {
  if (!user || firmId == null || firmId === '') return false;
  if (isPmpOrSiteAdminUser(user)) return true;
  return getUserFirmIds(user).includes(Number(firmId));
};

export const isClientAdminUser = (user?: FirmAccessUser | null): boolean =>
  ['client_admin', 'firm_admin', 'firm_main'].includes(user?.role || '') ||
  !!user?.roles?.some((role) => ['client_admin', 'firm_admin', 'firm_main'].includes(role.slug));

export const isClientFinanceUser = (user?: FirmAccessUser | null): boolean => {
  if (!user) return false;
  return user.is_finance_user === true || !!user.roles?.some((role) => role.slug === 'client_finance_user');
};

export const canManageClientInvoices = (user?: FirmAccessUser | null): boolean =>
  isPmpAdminUser(user);

export const canViewClientInvoices = (user?: FirmAccessUser | null): boolean =>
  canManageClientInvoices(user) || isClientAdminUser(user) || isClientFinanceUser(user);

export const isClientWalletRole = (user?: FirmAccessUser | null): boolean => {
  if (!user) return false;
  if (user.is_finance_user) return true;
  const slugs = [
    'client_admin',
    'client_user',
    'client_finance_user',
    'client_manager',
    'firm_admin',
    'firm_main',
  ];
  return slugs.includes(user.role || '') || !!user.roles?.some((role) => slugs.includes(role.slug));
};

export const canViewClientWallet = (
  user?: FirmAccessUser | null,
  firmId?: number | string | null
): boolean => {
  if (!user) return false;
  if (isPmpAdminUser(user)) return true;
  if (isSiteAdminUser(user)) return false;
  if (!userCanAccessFirm(user, firmId)) return false;
  return isClientWalletRole(user);
};

export const canDepositToClientWallet = (user?: FirmAccessUser | null): boolean =>
  isPmpAdminUser(user);

export const CLIENT_ACCESS_CONTACT_EMAILS = [
  'sohail@appelsolutions.com',
  'PMP@appelsolutions.com',
] as const;

export type ClientFirmAccessMode = 'full' | 'ceased' | 'download_only';

export interface FirmAccessNotice {
  mode: 'ceased' | 'download_only';
  stored_mode?: ClientFirmAccessMode;
  download_access_until?: string | null;
  firm_id?: number;
  firm_name?: string;
  message: string;
  contact_emails: string[];
}

export class FirmAccessSuspendedError extends Error {
  readonly code = 'firm_access_suspended';

  constructor(message?: string) {
    super(
      message ||
        'Your account is on hold. We\'re unable to sign you in right now. Please reach out to us at sohail@appelsolutions.com or PMP@appelsolutions.com and we will be glad to assist you.'
    );
    this.name = 'FirmAccessSuspendedError';
  }
}

export const canViewClientBilling = (user?: FirmAccessUser | null): boolean => {
  if (!user) return false;
  if (isPmpAdminUser(user)) return true;
  if (isSiteAdminUser(user)) return false;
  return isClientWalletRole(user);
};
